import React, { FC, memo, useCallback, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMedia } from "react-use";
import { useRouter } from "router";

import { Header } from "@/components/Header";
import { Section } from "@/components/Section";
import { Text } from "@/components/Text";
import { UserContext } from "@/components/User";
import { MEDIA_SIZE_MD } from "@/core/media";
import { fetchConsultantProfile, IConsultantProfile, TCompletenessItemKey } from "@/data/consultant-profiles";
import { Col, Flex, Row } from "@/eworkitComponents";
import { useFetcher } from "@/hooks/http";
import { BackButton } from "@/views/components/BackButton";
import { Container } from "@/views/components/Container";
import { useToasts } from "@/views/components/Toast/useToasts";
import { Error4XX } from "@/views/Errors";
import { router } from "@/views/router";

import { matchParamsType } from "../types";
import { Card, CompletenessModal, LevelWithProfile } from "./components";
import { isCompletenessItemCompleted } from "./service";

import "./_completeness.scss";

interface IItemMap {
  key: TCompletenessItemKey;
  icon: string;
  tooltip?: string;
  onClick?: () => void;
  isActive?: boolean;
  additionalText?: string;
}

type TIsCompletedFunc = (key: TCompletenessItemKey) => boolean;

export const getProfileElements = (
  isCompleted: TIsCompletedFunc,
  isOwner: boolean,
  isPrivateProfile: boolean,
): IItemMap[] => [
  { key: isPrivateProfile ? "ROLE" : "POSITION_AND_ROLE", icon: "user" },
  { key: "CV", icon: "file-tasks-1" },
  { key: "SKILL", icon: "tag" },
  { key: "LANGUAGE", icon: "planet" },
  { key: "LOCATION", icon: "location" },
  { key: "PHONE", icon: "phone-check" },
  {
    key: "ABOUT",
    icon: "interface-information",
    isActive: isOwner ? !isCompleted("ABOUT") : false,
    tooltip: !isOwner ? "CONSULTANT_PROFILES.DETAIL.ABOUT_ME.MANAGER_UNABLE_TO_EDIT" : "",
  },
  { key: "AVATAR", icon: "king" },
];

interface Props {
  profile: IConsultantProfile;
  update: (profile: IConsultantProfile) => void;
}

const BASE_CLASS = "completeness";

export const ConsultantProfilesCompletenessComponent: FC<Props> = memo(({ profile, update }) => {
  const { t } = useTranslation("session");
  const { addToast } = useToasts();
  const { hasConsultantProfile } = useContext(UserContext);
  const isMediumScreen = useMedia(MEDIA_SIZE_MD);
  const [open, setModalOpen] = useState(false);
  const [selectedSection, setSection] = useState<TCompletenessItemKey | null>(null);

  const isOwner = hasConsultantProfile(profile.id);

  const isPrivateProfile = !profile.companyRole;

  const handleUpdate = useCallback(
    (source: IConsultantProfile) => {
      addToast({ text: t("CONSULTANT_PROFILES.DETAIL.SUCCESS_MESSAGE"), type: "success" });

      update(source);
    },
    [selectedSection],
  );

  const {
    completeness: { completed },
  } = profile;

  const isCompleted = (section: TCompletenessItemKey) => isCompletenessItemCompleted(section, completed);

  const openSection = useCallback(
    (section: TCompletenessItemKey) => () => {
      if (!isCompleted(section)) {
        setSection(section);
        setModalOpen(true);
      }
    },
    [selectedSection, profile.id],
  );

  return (
    <>
      <div className={`${BASE_CLASS}__pattern-background`} />
      <Container>
        <Row direction="row">
          <Col margin={[false, false, "sm", false]}>
            <BackButton
              path={router.get("consultant-profiles.detail", { consultantProfileId: profile.id }).path}
              label={t("CONSULTANT_PROFILES.COMPLETENESS.BACK_TO_PROFILE")}
            />
          </Col>
        </Row>
        <Row>
          <Col width={{ xxs: 12 }}>
            <LevelWithProfile profile={profile} />
          </Col>
        </Row>
      </Container>
      <Section className={BASE_CLASS} background="background-main" padding={[false, false, "xl", false]} shadow="none">
        <Container>
          <Row>
            <Col width={{ xxs: 12 }}>
              <Flex
                align={isMediumScreen ? "flex-start" : "center"}
                innerMargin={isMediumScreen ? "sm" : "lg"}
                direction={isMediumScreen ? "column" : "row"}>
                <Header size={isMediumScreen ? "md" : "xl"}>
                  {t("CONSULTANT_PROFILES.COMPLETENESS.CONTENT.PROFILE.HEADER")}
                </Header>
                <Text color="grey1" className="completeness-section-description">
                  {t("CONSULTANT_PROFILES.COMPLETENESS.CONTENT.PROFILE.DESC")}
                </Text>
              </Flex>
            </Col>
          </Row>
          <Row padding={["lg", false, false]} wrap="wrap">
            {getProfileElements(isCompleted, isOwner, isPrivateProfile).map(
              ({ key, icon, isActive = !isCompleted(key), tooltip }) => {
                return (
                  <Col width={{ xxs: 12, md: 4 }} key={key}>
                    <Card
                      itemKey={key}
                      title={t(`CONSULTANT_PROFILES.COMPLETENESS.CONTENT.PROFILE.${key}.TITLE`)}
                      description={t(`CONSULTANT_PROFILES.COMPLETENESS.CONTENT.PROFILE.${key}.DESC`)}
                      additionalText={t(`CONSULTANT_PROFILES.COMPLETENESS.CONTENT.PROFILE.${key}.POINTS`)}
                      icon={icon}
                      isActive={isActive}
                      tooltip={t(tooltip || "")}
                      isDone={isCompleted(key)}
                      size={isMediumScreen ? "small" : "large"}
                      onClick={openSection(key)}
                      onUpdate={handleUpdate}
                    />
                  </Col>
                );
              },
            )}
          </Row>
        </Container>
      </Section>
      <CompletenessModal
        onClose={() => {
          setModalOpen(false);
        }}
        overflowX="auto"
        overflowY="auto"
        open={open}
        profile={profile}
        section={selectedSection}
        update={handleUpdate}
        size="sm"
      />
    </>
  );
});

export const ConsultantProfilesCompleteness: FC = memo(() => {
  const { contextCompany } = useContext(UserContext);
  const {
    matchParams: { consultantProfileId },
  } = useRouter({ ensureMatchParams: matchParamsType });

  const fetcher = useFetcher(fetchConsultantProfile, {
    companyId: contextCompany && contextCompany.id,
    consultantProfileId,
  });

  if (!fetcher.working && fetcher.error) {
    return <Error4XX />;
  }

  if (!fetcher.payload) {
    return null;
  }

  return <ConsultantProfilesCompletenessComponent profile={fetcher.payload} update={fetcher.updatePayload} />;
});
